import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '@/context/ProjectContext';
import { useRecipes } from '@/context/RecipeContext';
import type { ProjectListItem } from '@/types';
import { cn } from '@/lib/utils';

export function ProjectsPage() {
  const navigate = useNavigate();
  const { projects, isLoading, error, loadProjects } = useProjects();
  const { recipes, loadRecipes } = useRecipes();
  const [, setShowNewProjectModal] = useState(false);

  useEffect(() => {
    loadProjects();
    loadRecipes();
  }, [loadProjects, loadRecipes]);

  const handleProjectClick = (project: ProjectListItem) => {
    navigate(`/projects/${project.id}`);
  };

  const handleNewProject = () => {
    setShowNewProjectModal(true);
  };

  const handleStartFromRecipe = (recipeSlug: string) => {
    navigate(`/recipes?start=${recipeSlug}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'deployed':
        return 'bg-green-500/20 text-green-400';
      case 'active':
        return 'bg-blue-500/20 text-blue-400';
      case 'draft':
        return 'bg-zinc-500/20 text-zinc-400';
      case 'archived':
        return 'bg-zinc-700/20 text-zinc-500';
      default:
        return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  if (isLoading && projects.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Projects</h1>
          <p className="text-zinc-400 mt-1">Manage your website projects</p>
        </div>
        <button
          onClick={handleNewProject}
          className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors"
        >
          <span className="material-icons text-xl">add</span>
          New Project
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Empty State */}
      {projects.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
            <span className="material-icons text-3xl text-zinc-500">folder_open</span>
          </div>
          <h2 className="text-xl font-semibold text-zinc-200 mb-2">No projects yet</h2>
          <p className="text-zinc-400 mb-6 text-center max-w-md">
            Create your first project from scratch or start with a recipe template.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleNewProject}
              className="px-4 py-2 bg-zinc-700 text-zinc-200 rounded-lg hover:bg-zinc-600 transition-colors"
            >
              Blank Project
            </button>
            <button
              onClick={() => navigate('/recipes')}
              className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors"
            >
              Browse Recipes
            </button>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project)}
              className="bg-elevated rounded-lg border border-zinc-800 p-4 cursor-pointer hover:border-zinc-700 transition-colors group"
            >
              {/* Project thumbnail placeholder */}
              <div className="aspect-video bg-zinc-800 rounded-lg mb-4 flex items-center justify-center">
                <span className="material-icons text-4xl text-zinc-600">web</span>
              </div>

              {/* Project info */}
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-zinc-100 group-hover:text-brand transition-colors">
                    {project.name}
                  </h3>
                  <span className={cn('px-2 py-0.5 text-xs rounded-full', getStatusColor(project.status))}>
                    {project.status}
                  </span>
                </div>

                {project.description && (
                  <p className="text-sm text-zinc-400 line-clamp-2">{project.description}</p>
                )}

                {project.recipe_name && (
                  <div className="flex items-center gap-1 text-xs text-zinc-500">
                    <span className="material-icons text-sm">auto_awesome</span>
                    {project.recipe_name}
                  </div>
                )}

                <div className="flex items-center gap-4 text-xs text-zinc-500 pt-2">
                  <span className="flex items-center gap-1">
                    <span className="material-icons text-sm">chat</span>
                    {project.conversation_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="material-icons text-sm">description</span>
                    {project.artifact_count}
                  </span>
                  <span className="ml-auto">
                    {new Date(project.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Start Recipes */}
      {projects.length > 0 && recipes.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Quick Start</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {recipes.slice(0, 4).map((recipe) => (
              <button
                key={recipe.id}
                onClick={() => handleStartFromRecipe(recipe.slug)}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors whitespace-nowrap"
              >
                <span className="material-icons text-brand">{recipe.icon || 'auto_awesome'}</span>
                <span className="text-zinc-300">{recipe.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
